import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { usePrevioCore, $t } from '@previo/core'
import { ProxyP1routes } from './ProxyP1routes'
import { Routes } from './routes'
import { Layouts } from '~/layouts'
import loadUser from '~/router/middleware/loadUser'
import { usePropertyStore } from '~/store/property/property'
import loadSelectedProperty from '~/router/middleware/selectedProperty'
import authorization from '~/router/middleware/authentication'
import handleUseNewVersionQueryParam from '~/router/middleware/handleUseNewVersionQueryParam'
import refreshPreferredVersion from '~/router/middleware/refreshPreferredVersion'
import checkPreferredVersion from '~/router/middleware/checkPreferredVersion'
import checkMyConnectionEditFlag from '~/router/middleware/checkMyConnectionEditFlag'
import checkAgentFlag from '~/router/middleware/checkAgentFlag'
import checkServiceMappingFlag from '~/router/middleware/checkServiceMappingFlag'
import checkWhatsAppFlag from '~/router/middleware/checkWhatsAppFlag'
import checkConnectedRoomsFlag from '~/router/middleware/checkConnectedRoomsFlag'
import checkTaxSettingsFlag from '~/router/middleware/checkTaxSettingsFlag'
import { Module } from '~/codebooks/acl/Module'
import { Privilege } from '~/codebooks/acl/Privilege'
import checkACLGuard from '~/router/guard/checkACL'
import checkLicenseGuard from '~/router/guard/checkLicense'
import { loadObjectKinds } from '~/router/middleware/loadObjectKinds'
import { CampaignCategoryId } from '~/pages/communication-template/codebooks/CampaignCategory'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: Routes.ENTRYPOINT_LOGIN_INDEX,
      meta: { layout: Layouts.AUTH_WITHOUT_MENU, title: () => pageTitle($t('Login')) },
      component: () => import('~/pages/general/login/Index.vue'),
    },
    {
      path: '/demo/unlayer',
      name: 'demo-unlayer',
      meta: { layout: Layouts.NO_AUTH, title: () => pageTitle($t('Demo Unlayer')) },
      component: () => import('~/pages/demo/unlayer/Index.vue'),
    },
    {
      path: '/:propertyId(\\d+)?',
      beforeEnter: [
        handleUseNewVersionQueryParam,
        refreshPreferredVersion,
        authorization,
        loadSelectedProperty,
        loadUser,
      ],
      redirect(to) {
        to.name = Routes.CALENDAR_INDEX

        return to
      },
      children: [
        {
          path: 'calendar',
          name: Routes.CALENDAR_INDEX,
          meta: {
            layout: Layouts.AUTH,
            P1Route: Routes.CALENDAR_INDEX_P1_P2,
            requiredModule: Module.RESERVATION_CANVAS,
            title: () => pageTitle($t('Calendar')),
          },
          beforeEnter: [checkPreferredVersion, loadObjectKinds],
          component: () => import('~/pages/reservation/calendar/Index.vue'),
          children: [
            {
              path: ':calendarId(\\d+)?',
              name: Routes.CALENDAR_SCHEDULE_INDEX,
              components: {
                default: () => import('~/pages/reservation/calendar/pages/schedule/Index.vue'),
                toolbar: () => import('~/pages/reservation/calendar/pages/schedule/Toolbar.vue'),
              },
            },
            {
              path: 'availability',
              name: Routes.CALENDAR_AVAILABILITY_INDEX,
              components: {
                default: () => import('~/pages/reservation/calendar/pages/availability/Index.vue'),
                toolbar: () =>
                  import('~/pages/reservation/calendar/pages/availability/Toolbar.vue'),
              },
            },
            {
              path: 'unassigned',
              name: Routes.CALENDAR_UNASSIGNED_INDEX,
              components: {
                default: () => import('~/pages/reservation/calendar/pages/unassigned/Index.vue'),
                toolbar: () => import('~/pages/reservation/calendar/pages/unassigned/Toolbar.vue'),
              },
            },
          ],
        },
        {
          path: 'reservations',
          name: Routes.RESERVATIONS_INDEX,
          meta: {
            layout: Layouts.AUTH,
            P1Route: Routes.RESERVATIONS_INDEX_P1_P2,
            requiredModule: Module.RESERVATION_TABLE,
            title: () => pageTitle($t('Reservations')),
          },
          beforeEnter: [checkPreferredVersion],
          component: () => import('~/pages/reservation/list/Index.vue'),
        },
        {
          path: 'dashboard',
          name: Routes.DASHBOARD_INDEX,
          meta: {
            layout: Layouts.AUTH,
            requiredModule: Module.REPORTS_DASHBOARD,
            title: () => pageTitle($t('Dashboard')),
          },
          component: () => import('~/pages/dashboard/Index.vue'),
        },
        {
          path: 'ai',
          name: Routes.AI_INDEX,
          meta: {
            layout: Layouts.AUTH,
            title: () => pageTitle($t('AI assistant')),
          },
          beforeEnter: [checkAgentFlag],
          component: () => import('~/pages/ai/Index.vue'),
        },
        {
          path: 'alfred-ai',
          name: Routes.ALFRED_AI_INDEX,
          meta: {
            layout: Layouts.AUTH,
            title: () => pageTitle($t('Alfred AI')),
          },
          beforeEnter: [checkAgentFlag],
          component: () => import('~/pages/ai-advisor/Index.vue'),
        },
        {
          path: 'ai-advisor',
          /**
           * The AI advisor was renamed to Alfred AI, old links keep working.
           */
          redirect: { name: Routes.ALFRED_AI_INDEX },
        },
        {
          path: 'reservations/:reservationId(\\d+)',
          name: Routes.RESERVATIONS_DETAIL,
          meta: {
            layout: Layouts.AUTH_WITHOUT_MENU,
            requiredModule: Module.RESERVATION,
            title: () => pageTitle($t('Reservation detail')),
          },
          component: () => import('~/pages/reservation/detail/Index.vue'),
        },

        {
          path: 'contact-person',
          /**
           * Workaround for staging env-cached URL: redirecting from the old module path to the new one.
           */
          redirect: { name: Routes.CONTACT_PERSONS_INDEX },
        },

        {
          path: 'contact-persons',
          name: Routes.CONTACT_PERSONS_INDEX,
          meta: {
            layout: Layouts.AUTH,
            requiredModule: Module.CONTACT_PERSONS_GRID,
            P1Route: Routes.CONTACT_PERSONS_INDEX_P1_P2,
            title: () => pageTitle($t('Contact person')),
          },
          component: () => import('~/pages/contact-person/Index.vue'),
        },

        {
          path: 'offers',
          name: Routes.OFFERS_INDEX,
          meta: {
            layout: Layouts.AUTH,
            requiredModule: Module.OFFERS_GRID,
            title: () => pageTitle($t('Offers')),
          },
          component: () => import('~/pages/offer/Index.vue'),
        },

        {
          path: 'companies',
          name: Routes.COMPANIES_INDEX,
          meta: {
            layout: Layouts.AUTH,
            requiredModule: Module.COMPANIES_GRID,
            title: () => pageTitle($t('Companies')),
          },
          component: () => import('~/pages/company/Index.vue'),
        },

        {
          path: 'statoffice-reports-cz',
          name: Routes.STATOFFICE_REPORT_CZ_INDEX,
          meta: {
            layout: Layouts.AUTH,
            requiredModule: Module.REPORTS_FOR_STAT_AUTH,
            title: () => pageTitle($t('ČSÚ reports')),
          },
          component: () => import('~/pages/stat-office-report/cz/Index.vue'),
        },

        {
          // Reachable only by URL for now — the Ntak menu item still points to
          // the P1 page until #55991 wires this page to the BE.
          path: 'reports/ntak',
          name: Routes.REPORTS_NTAK_REPORT_INDEX,
          meta: {
            layout: Layouts.AUTH,
            requiredModule: Module.REPORTS_NTAK_REPORT,
            title: () => pageTitle($t('NTAK report')),
          },
          component: () => import('~/pages/reports/ntak/Index.vue'),
        },

        {
          path: 'guests',
          name: Routes.GUESTS_INDEX,
          meta: {
            layout: Layouts.AUTH,
            requiredModule: Module.GUESTS_GRID,
            title: () => pageTitle($t('Guest')),
          },
          component: () => import('~/pages/guest/Index.vue'),
        },

        {
          path: 'invoicing/documents',
          name: Routes.INVOICING_DOCUMENTS_INDEX,
          meta: {
            layout: Layouts.AUTH,
            requiredModule: Module.PAYMENTS_INVOICING,
            P1Route: Routes.INVOICING_DOCUMENTS_INDEX_P1_P2,
            title: () => pageTitle($t('Invoices')),
          },
          beforeEnter: [checkPreferredVersion],
          component: () => import('~/pages/invoicing/documents/Index.vue'),
        },
        {
          path: 'invoicing/documents/:documentId(\\d+)',
          name: Routes.INVOICING_DOCUMENTS_EDIT,
          meta: {
            layout: Layouts.AUTH_WITHOUT_MENU,
            requiredModule: Module.PAYMENTS_INVOICING,
            P1Route: Routes.INVOICING_DOCUMENTS_INDEX_P1_P2,
            title: () => pageTitle($t('Edit document')),
          },
          component: () => import('~/pages/invoicing/documents/Edit.vue'),
        },

        {
          path: 'invoicing/gift-vouchers',
          name: Routes.INVOICING_GIFT_VOUCHERS_INDEX,
          meta: {
            layout: Layouts.AUTH,
            requiredModule: Module.PAYMENTS_GIFT_VOUCHERS,
            title: () => pageTitle($t('Gift vouchers')),
          },
          component: () => import('~/pages/invoicing/gift-vouchers/Index.vue'),
        },
        {
          path: 'invoicing/gift-vouchers/:giftVoucherId(\\d+)',
          name: Routes.INVOICING_GIFT_VOUCHERS_DETAIL,
          meta: {
            layout: Layouts.AUTH_WITHOUT_MENU,
            requiredModule: Module.PAYMENTS_GIFT_VOUCHERS,
            title: () => pageTitle($t('Gift voucher')),
          },
          component: () => import('~/pages/invoicing/gift-vouchers/detail/Index.vue'),
        },

        {
          path: 'invoicing/payments/cashbook',
          name: Routes.INVOICING_PAYMENTS_CASH_BOOK_INDEX,
          meta: {
            layout: Layouts.AUTH,
            requiredModule: Module.PAYMENTS_CASHBOOK_GRID,
            title: () => pageTitle($t('Payments')),
          },
          component: () => import('~/pages/invoicing/payment/Index.vue'),
        },

        {
          path: 'invoicing/payments/bank-transfer',
          name: Routes.INVOICING_PAYMENTS_BANK_TRANSFER_INDEX,
          meta: {
            layout: Layouts.AUTH,
            requiredModule: Module.PAYMENTS_BANKTRANSFERS_GRID,
            title: () => pageTitle($t('Payments')),
          },
          component: () => import('~/pages/invoicing/payment/Index.vue'),
        },

        {
          path: 'invoicing/payments/terminal-payment',
          name: Routes.INVOICING_PAYMENTS_TERMINAL_PAYMENT_INDEX,
          meta: {
            layout: Layouts.AUTH,
            requiredModule: Module.PAYMENTS_TERMINAL_GRID,
            title: () => pageTitle($t('Payments')),
          },
          component: () => import('~/pages/invoicing/payment/Index.vue'),
        },

        {
          path: 'invoicing/payments/online-payment',
          name: Routes.INVOICING_PAYMENTS_ONLINE_PAYMENT_INDEX,
          meta: {
            layout: Layouts.AUTH,
            requiredModule: Module.PAYMENTS_ONLINE_GRID,
            title: () => pageTitle($t('Payments')),
          },
          component: () => import('~/pages/invoicing/payment/Index.vue'),
        },

        {
          path: 'invoicing/payments/coupon',
          name: Routes.INVOICING_PAYMENTS_COUPON_INDEX,
          meta: {
            layout: Layouts.AUTH,
            requiredModule: Module.PAYMENTS_COUPONS_GRID,
            title: () => pageTitle($t('Payments')),
          },
          component: () => import('~/pages/invoicing/payment/Index.vue'),
        },

        {
          path: 'settings/applications/online-reservations/booking',
          meta: { layout: Layouts.AUTH_SETTING, requiredModule: Module.ONLINE_RESERVATION_BOOKING },
          component: () =>
            import('~/pages/setting/applications/online-reservation/booking/BookingWrapper.vue'),
          children: [
            {
              path: 'general',
              name: Routes.SETTINGS_ONLINE_RESERVATIONS_BOOKING_GENERAL,
              meta: {
                layout: Layouts.AUTH_SETTING,
                requiredModule: Module.ONLINE_RESERVATION_BOOKING,
                title: () => pageTitle($t('General')),
              },
              component: () =>
                import(
                  '~/pages/setting/applications/online-reservation/booking/components/general/Index.vue'
                ),
            },
            {
              path: 'conversion',
              name: Routes.SETTINGS_ONLINE_RESERVATIONS_BOOKING_CONVERSION,
              meta: {
                layout: Layouts.AUTH_SETTING,
                requiredModule: Module.ONLINE_RESERVATION_BOOKING,
                title: () => pageTitle($t('Conversion')),
              },
              component: () =>
                import(
                  '~/pages/setting/applications/online-reservation/booking/components/conversionMeasure/Index.vue'
                ),
            },
            {
              path: 'form-configuration',
              name: Routes.SETTINGS_ONLINE_RESERVATIONS_BOOKING_FORM_CONFIGURATION,
              meta: {
                layout: Layouts.AUTH_SETTING,
                requiredModule: Module.ONLINE_RESERVATION_BOOKING,
                title: () => pageTitle($t('Form configuration')),
              },
              component: () =>
                import(
                  '~/pages/setting/applications/online-reservation/booking/components/formConfiguration/Index.vue'
                ),
            },
          ],
        },

        {
          path: 'settings/applications/self-service-bar',
          name: Routes.SELF_SERVICE_BAR_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            requiredModule: Module.SETTINGS_SELF_SERVICE_BAR,
            title: () => pageTitle($t('Self-service bar')),
          },
          component: () => import('~/pages/setting/applications/self-service-bar/General.vue'),
        },
        {
          path: 'settings/applications/self-service-bar/:barId(.*)?/preview/load/pdf',
          name: Routes.SELF_SERVICE_BAR_QR_PREVIEW_LOAD_PDF,
          meta: {
            layout: Layouts.AUTH_WITHOUT_MENU,
            requiredModule: Module.SETTINGS_SELF_SERVICE_BAR,
            title: () => pageTitle($t('Self service bar - preview')),
          },
          component: () =>
            import('~/pages/setting/applications/self-service-bar/PreviewLoadPDF.vue'),
        },
        {
          path: 'settings/applications/pos',
          name: Routes.POS_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            requiredModule: Module.SETTINGS_POS,
            title: () => pageTitle($t('POS')),
          },
          component: () => import('~/pages/setting/applications/pos/General.vue'),
        },
        {
          path: 'settings/applications/connected-rooms',
          name: Routes.SETTINGS_CONNECTED_ROOMS_CONNECTIONS_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            requiredModule: Module.SETTINGS_HOTEL_ROOMS,
            title: () => pageTitle($t('Connected rooms')),
          },
          beforeEnter: [checkConnectedRoomsFlag],
          component: () =>
            import('~/pages/setting/applications/connected-rooms/connections/Index.vue'),
        },
        {
          path: 'settings/applications/connected-rooms/general',
          name: Routes.SETTINGS_CONNECTED_ROOMS_GENERAL_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            requiredModule: Module.SETTINGS_HOTEL_ROOMS,
            title: () => pageTitle($t('Connected rooms')),
          },
          beforeEnter: [checkConnectedRoomsFlag],
          component: () => import('~/pages/setting/applications/connected-rooms/general/Index.vue'),
        },
        {
          path: 'housekeeping/cleaning',
          name: Routes.HOUSEKEEPING_INDEX,
          meta: {
            layout: Layouts.AUTH,
            requiredModule: Module.HOUSEKEEPING_CLEANING_GRID,
            title: () => pageTitle($t('Cleaning')),
          },
          component: () => import('~/pages/housekeeping/cleaning/Index.vue'),
        },
        {
          path: 'housekeeping/defects',
          name: Routes.DEFECTS_INDEX,
          meta: {
            layout: Layouts.AUTH,
            requiredModule: Module.HOUSEKEEPING_DEFECT_GRID,
            title: () => pageTitle($t('Defects')),
          },
          component: () => import('~/pages/housekeeping/defect/Index.vue'),
        },
        {
          path: 'housekeeping/tasks',
          name: Routes.TASKS_INDEX,
          meta: {
            layout: Layouts.AUTH,
            title: () => pageTitle($t('Tasks')),
          },
          component: () => import('~/pages/housekeeping/task/Index.vue'),
        },
        {
          path: 'housekeeping/task-templates',
          name: Routes.TASK_TEMPLATES_INDEX,
          meta: {
            layout: Layouts.AUTH,
            title: () => pageTitle($t('Automatic tasks')),
          },
          component: () => import('~/pages/housekeeping/task-template/Index.vue'),
        },
        {
          path: 'settings/applications/offers',
          name: Routes.SETTINGS_OFFER_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            requiredModule: Module.OFFERS_GRID,
            title: () => pageTitle($t('Offers')),
          },
          component: () => import('~/pages/setting/applications/offers/Index.vue'),
        },
        {
          path: 'settings/applications/housekeeping/general',
          name: Routes.SETTINGS_HOUSEKEEPING_GENERAL_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            requiredModule: Module.HOUSEKEEPING_CLEANING_GRID,
            title: () => pageTitle($t('General')),
          },
          component: () => import('~/pages/setting/applications/housekeeping/general/Index.vue'),
        },
        {
          path: 'settings/applications/housekeeping/app',
          name: Routes.SETTINGS_HOUSEKEEPING_APP_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            requiredModule: Module.HOUSEKEEPING_CLEANING_GRID,
            title: () => pageTitle($t('Housekeeping app')),
          },
          component: () => import('~/pages/setting/applications/housekeeping/app/Index.vue'),
        },
        {
          path: 'settings/applications/alfred/general',
          name: Routes.SETTINGS_ALFRED_GENERAL_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            requiredModule: Module.SETTINGS_ALFRED_APP,
            title: () => pageTitle($t('Alfred')),
          },
          component: () => import('~/pages/setting/applications/alfred/General.vue'),
        },
        {
          path: 'settings/applications/alfred/trip-tips',
          name: Routes.SETTINGS_ALFRED_TRIP_TIPS,
          meta: {
            layout: Layouts.AUTH_SETTING,
            requiredModule: Module.SETTINGS_ALFRED_APP,
            title: () => pageTitle($t('Alfred activities')),
          },
          component: () => import('~/pages/setting/applications/alfred/TripTip.vue'),
        },
        {
          path: 'settings/lock-systems/set-up',
          name: Routes.SETTINGS_LOCK_SYSTEMS_SETUP,
          meta: {
            layout: Layouts.AUTH_SETTING,
            requiredModule: Module.SETTINGS_LOCK_SYSTEMS_SETUP,
            title: () => pageTitle($t('Lock Systems Connection Set-up')),
          },
          component: () => import('~/pages/setting/applications/lock-systems/setup/Index.vue'),
        },
        {
          path: 'settings/lock-systems/settings',
          name: Routes.SETTINGS_LOCK_SYSTEMS_SETTINGS,
          meta: {
            layout: Layouts.AUTH_SETTING,
            requiredModule: Module.SETTINGS_LOCK_SYSTEMS,
            title: () => pageTitle($t('Lock Systems Settings')),
          },
          component: () => import('~/pages/setting/applications/lock-systems/settings/Index.vue'),
        },
        {
          path: 'communication-templates/automated',
          name: Routes.MAILING_INDEX_AUTOMATED,
          meta: {
            layout: Layouts.AUTH,
            requiredModule: Module.MAILING,
            campaignCategory: CampaignCategoryId.AUTOMATED,
            title: () => pageTitle($t('Communication templates')),
          },
          component: () => import('~/pages/communication-template/Index.vue'),
        },
        {
          path: 'communication-templates/marketing',
          name: Routes.MAILING_INDEX_MARKETING,
          meta: {
            layout: Layouts.AUTH,
            requiredModule: Module.MAILING,
            campaignCategory: CampaignCategoryId.MARKETING,
            title: () => pageTitle($t('Communication templates')),
          },
          component: () => import('~/pages/communication-template/Index.vue'),
        },
        {
          path: 'communication-templates/detail/:campaignId(.*)?/:tab(settings|translations|statistics|sent-messages)',
          name: Routes.MAILING_DETAIL,
          meta: {
            layout: Layouts.AUTH_WITHOUT_MENU,
            requiredModule: Module.MAILING,
            title: () => pageTitle($t('Campaign settings')),
          },
          component: () => import('~/pages/communication-template/components/CampaignSettings.vue'),
        },
        {
          path: 'general/no-object-rooms',
          name: Routes.GENERAL_NO_OBJECT_ROOMS,
          meta: { layout: Layouts.AUTH, title: () => pageTitle($t('No object rooms')) },
          component: () => import('~/pages/general/no-object-rooms/Index.vue'),
        },
        {
          path: 'settings/prices/visitor-tax',
          name: Routes.VISITOR_TAX_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            requiredModule: Module.PRICES_TAX_SETTINGS,
            P1Route: Routes.VISITOR_TAX_INDEX_P1,
            title: () => pageTitle($t('Taxes & fees')),
          },
          beforeEnter: [checkTaxSettingsFlag],
          component: () => import('~/pages/setting/prices/visitor-tax/Index.vue'),
        },
        {
          path: 'settings/applications/guests-and-companies/general',
          name: Routes.SETTINGS_GUESTS_AND_COMPANIES_GENERAL_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            title: () => pageTitle($t('General')),
          },
          component: () =>
            import('~/pages/setting/applications/guests-and-companies/general/Index.vue'),
        },
        {
          path: 'settings/applications/guests-and-companies/guest-labels',
          name: Routes.SETTINGS_GUESTS_AND_COMPANIES_GUEST_LABELS_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            requiredModule: Module.GUESTS_GRID,
            title: () => pageTitle($t('Guest labels')),
          },
          component: () =>
            import('~/pages/setting/applications/guests-and-companies/guest/guest-tags/Index.vue'),
        },
        {
          path: 'settings/applications/guests-and-companies/company-labels',
          name: Routes.SETTINGS_GUESTS_AND_COMPANIES_COMPANY_LABELS_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            requiredModule: Module.COMPANIES_GRID,
            title: () => pageTitle($t('Company labels')),
          },
          component: () =>
            import(
              '~/pages/setting/applications/guests-and-companies/company/company-tags/Index.vue'
            ),
        },
        {
          path: 'settings/applications/guests-and-companies/agency',
          name: Routes.SETTINGS_GUESTS_AND_COMPANIES_AGENCY_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            title: () => pageTitle($t('Agency system')),
          },
          component: () =>
            import('~/pages/setting/applications/guests-and-companies/company/agency/Index.vue'),
        },
        {
          path: 'settings/applications/channel-manager/my-connections',
          name: Routes.SETTINGS_CHANNEL_MANAGER_MY_CONNECTIONS_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            requiredModule: Module.SETTINGS_ONLINE_RESERVATION_PREVIO_PARTNERS,
            title: () => pageTitle($t('My connections')),
          },
          component: () =>
            import('~/pages/setting/applications/channel-manager/my-connections/Index.vue'),
        },
        {
          path: 'settings/applications/channel-manager/my-connections/:otaId/profile/:profileId',
          name: Routes.SETTINGS_CHANNEL_MANAGER_MY_CONNECTIONS_DETAIL,
          meta: {
            layout: Layouts.AUTH_WITHOUT_MENU,
            requiredModule: Module.SETTINGS_ONLINE_RESERVATION_PREVIO_PARTNERS,
            title: () => pageTitle($t('Connection detail')),
          },
          component: () =>
            import(
              '~/pages/setting/applications/channel-manager/my-connections/ConnectionDetail.vue'
            ),
        },
        {
          path: 'settings/applications/channel-manager/my-connections/:otaId/profile/:profileId/next',
          name: Routes.SETTINGS_CHANNEL_MANAGER_MY_CONNECTIONS_DETAIL_NEXT,
          meta: {
            layout: Layouts.AUTH_WITHOUT_MENU,
            requiredModule: Module.SETTINGS_ONLINE_RESERVATION_PREVIO_PARTNERS,
            title: () => pageTitle($t('Connection detail')),
          },
          beforeEnter: [checkMyConnectionEditFlag],
          component: () =>
            import(
              '~/pages/setting/applications/channel-manager/my-connections/connection-detail-next/ConnectionDetailNext.vue'
            ),
        },
        {
          path: 'settings/applications/channel-manager/my-connections/yield-planet/:yieldPlanetPartnerId',
          name: Routes.SETTINGS_CHANNEL_MANAGER_MY_CONNECTIONS_YIELD_PLANET_DETAIL,
          meta: {
            layout: Layouts.AUTH_WITHOUT_MENU,
            requiredModule: Module.SETTINGS_ONLINE_RESERVATION_PREVIO_PARTNERS,
            title: () => pageTitle($t('YieldPlanet connection detail')),
          },
          component: () =>
            import(
              '~/pages/setting/applications/channel-manager/my-connections/YieldPlanetConnectionDetail.vue'
            ),
        },
        {
          path: 'settings/applications/channel-manager/service-mapping',
          name: Routes.SETTINGS_CHANNEL_MANAGER_SERVICE_MAPPING_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            requiredModule: Module.SETTINGS_ONLINE_RESERVATION_PREVIO_PARTNERS,
            title: () => pageTitle($t('Service mapping')),
          },
          beforeEnter: [checkServiceMappingFlag],
          component: () =>
            import('~/pages/setting/applications/channel-manager/service-mapping/Index.vue'),
        },
        {
          path: 'settings/applications/channel-manager/marketplace',
          name: Routes.SETTINGS_CHANNEL_MANAGER_MARKETPLACE_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            title: () => pageTitle($t('Marketplace')),
          },
          component: () =>
            import('~/pages/setting/applications/channel-manager/marketplace/Index.vue'),
        },

        {
          path: 'settings/applications/accounting/documents',
          name: Routes.SETTINGS_ACCOUNTING_DOCUMENTS_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            isMobileFirst: true,
            title: () => pageTitle($t('Documents')),
          },
          component: () => import('~/pages/setting/applications/accounting/documents/Index.vue'),
        },
        {
          path: 'settings/applications/accounting/gift-vouchers',
          name: Routes.SETTINGS_ACCOUNTING_GIFT_VOUCHERS_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            isMobileFirst: true,
            title: () => pageTitle($t('Gift vouchers')),
            requiredModule: Module.PAYMENTS_GIFT_VOUCHERS,
          },
          component: () =>
            import('~/pages/setting/applications/accounting/gift-vouchers/Index.vue'),
        },

        {
          path: 'settings/applications/accounting/rounding',
          name: Routes.SETTINGS_ACCOUNTING_ROUNDING_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            isMobileFirst: true,
            title: () => pageTitle($t('Rounding')),
          },
          component: () => import('~/pages/setting/applications/accounting/rounding/Index.vue'),
        },
        {
          path: 'settings/applications/accounting/payments',
          name: Routes.SETTINGS_ACCOUNTING_PAYMENTS_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            title: () => pageTitle($t('Payments numbering')),
          },
          component: () => import('~/pages/setting/applications/accounting/payments/Index.vue'),
        },
        {
          path: 'settings/applications/accounting/exports/software',
          name: Routes.SETTINGS_ACCOUNTING_EXPORTS_SOFTWARE_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            title: () => pageTitle($t('Accounting softwares')),
          },
          component: () =>
            import('~/pages/setting/applications/accounting/exports/software/Index.vue'),
        },
        {
          path: 'settings/applications/accounting/accepted-payments-types',
          name: Routes.SETTINGS_ACCOUNTING_ACCEPTED_PAYMENTS_TYPES_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            title: () => pageTitle($t('Accepted payments types')),
            requiredModule: Module.PAYMENTS_GRID,
          },
          component: () =>
            import('~/pages/setting/applications/accounting/accepted-payments-types/Index.vue'),
        },
        {
          path: 'settings/reservations/general',
          name: Routes.SETTINGS_RESERVATIONS_GENERAL,
          meta: {
            layout: Layouts.AUTH_SETTING,
            title: () => pageTitle($t('Reservation general settings')),
          },
          component: () => import('~/pages/setting/applications/reservation/general/General.vue'),
        },
        {
          path: 'settings/reservations/folio',
          name: Routes.SETTINGS_RESERVATIONS_FOLIO,
          meta: {
            layout: Layouts.AUTH_SETTING,
            title: () => pageTitle($t('Reservation folio settings')),
          },
          component: () => import('~/pages/setting/applications/reservation/folio/Folio.vue'),
        },
        {
          path: 'settings/reservations/voucher',
          name: Routes.SETTINGS_RESERVATIONS_VOUCHER,
          meta: {
            layout: Layouts.AUTH_SETTING,
            title: () => pageTitle($t('Reservation voucher settings')),
          },
          component: () => import('~/pages/setting/applications/reservation/voucher/Voucher.vue'),
        },
        {
          path: 'settings/prices/currencies',
          name: Routes.EXCHANGE_RATES_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            requiredModule: Module.PRICES_CURRENCIES_EXCHANGE_RATES,
            P1Route: Routes.EXCHANGE_RATES_INDEX_P1,
            title: () => pageTitle($t('Currencies & exchange rates')),
          },
          component: () => import('~/pages/setting/prices/currencies/Index.vue'),
        },
        {
          path: 'settings/hotel/email',
          name: Routes.SETTINGS_HOTEL_EMAIL,
          meta: {
            layout: Layouts.AUTH_SETTING,
            title: () => pageTitle($t('Email settings')),
          },
          component: () => import('~/pages/setting/hotel/email/Email.vue'),
        },
        {
          path: 'settings/hotel/whatsapp',
          name: Routes.SETTINGS_HOTEL_WHATSAPP,
          meta: {
            layout: Layouts.AUTH_SETTING,
            requiredModule: Module.SETTINGS_EMAIL_SETTINGS,
            title: () => pageTitle($t('WhatsApp settings')),
          },
          beforeEnter: [checkWhatsAppFlag],
          component: () => import('~/pages/setting/hotel/whatsapp/WhatsApp.vue'),
        },
        {
          path: 'settings/hotel/reports/csu',
          name: Routes.SETTINGS_HOTEL_REPORTS_CSU,
          meta: {
            layout: Layouts.AUTH_SETTING,
            title: () => pageTitle($t('ČSÚ reports settings')),
            requiredModule: Module.REPORTS_FOR_STAT_AUTH,
          },
          component: () => import('~/pages/setting/hotel/reports/csu/Csu.vue'),
        },
        {
          path: 'settings/applications/accounting/exports/vat-breakdown',
          name: Routes.SETTINGS_ACCOUNTING_EXPORTS_VAT_BREAKDOWN_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            title: () => pageTitle($t('VAT breakdown')),
          },
          component: () =>
            import('~/pages/setting/applications/accounting/exports/vat-breakdown/Index.vue'),
        },
        {
          path: 'settings/notifications/reservation',
          name: Routes.SETTINGS_NOTIFICATIONS_RESERVATION,
          meta: {
            layout: Layouts.AUTH_SETTING,
            title: () => pageTitle($t('Reservation notifications')),
          },
          component: () => import('~/pages/setting/notifications/reservation/Index.vue'),
        },
        {
          path: 'settings/dashboard',
          name: Routes.SETTINGS_DASHBOARD_INDEX,
          meta: {
            layout: Layouts.AUTH_SETTING,
            title: () => pageTitle($t('Settings dashboard')),
          },
          component: () => import('~/pages/setting/dashboard/Index.vue'),
        },
        {
          path: 'help/manual',
          name: Routes.HELP_MANUAL_INDEX,
          redirect() {
            const { useLocale } = usePrevioCore()
            const currentLocale = useLocale().get()
            const url: string = 'https://help.previo.app/'
            const availableManualLanguages = ['cs', 'en', 'pl', 'hu', 'ro']
            const lang = availableManualLanguages.includes(currentLocale) ? currentLocale : 'en'

            window.open(url + lang, '_blank')

            return {
              name: '',
            }
          },
        },
        {
          path: 'p1-pms',
          meta: { layout: Layouts.NO_AUTH },
          children: ProxyP1routes,
        },
      ],
    },
    {
      path: '/general/select-property',
      name: Routes.GENERAL_SELECT_PROPERTY,
      meta: {
        layout: Layouts.AUTH_WITHOUT_PROPERTY,
        title: () => pageTitle($t('Select property')),
      },
      component: () => import('~/pages/general/select-property/Index.vue'),
    },
    {
      path: '/:pathMatch(.*)*',
      name: Routes.GENERAL_NOT_FOUND,
      meta: { layout: Layouts.NO_AUTH, title: () => pageTitle($t('Page not found')) },
      component: () => import('~/pages/general/404/Index.vue'),
    },
  ] as Readonly<RouteRecordRaw[]>,
})

const pageTitle = (moduleTitle: string): string => {
  const propertyStore = usePropertyStore()
  const property = propertyStore.property
  const hotelName = property?.name
  const hotelId = property?.propertyId

  if (hotelName && hotelId) {
    return `${moduleTitle} (${hotelName} - ${hotelId})`
  }

  return moduleTitle
}

router.beforeResolve(checkACLGuard)
router.beforeResolve(checkLicenseGuard)

router.afterEach((to) => {
  document.title = to.meta.title()
})

export default router

declare module 'vue-router' {
  interface RouteMeta {
    layout: Layouts
    isMobileFirst?: boolean
    P1Route?: Routes // This allows the user to switch to the P1 version of the route in the menu
    requiredModule?: Module
    requiredPrivilege?: Privilege
    campaignCategory?: CampaignCategoryId
    title: () => string
  }
}
